import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { EtatContratArtiste } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
export interface ContratArtistePresente {
  id: string;
  artisteId: string;
  debutLe: string;
  finLe: string | null;
  exclusivite: boolean;
  commission: string;
  territoire: string;
  etat: EtatContratArtiste;
  echu: boolean;
  joursAvantEcheance: number | null;
  resilieLe: string | null;
  motifResiliation: string;
  rappelsJours: number[];
  contratGenereId: string | null;
  documentId: string | null;
  note: string;
  creeLe: string;
  modifieLe: string;
}
export interface CorpsContratArtiste {
  artisteId: string;
  debutLe: string;
  finLe?: string | null;
  exclusivite?: boolean;
  commission?: string;
  territoire?: string;
  rappelsJours?: number[];
  note?: string;
}
export interface FiltresContratsArtistes {
  artisteId?: string;
  etat?: EtatContratArtiste;
  echeanceAvant?: string;
  page?: number;
  parPage?: number;
}
const racine = (g: string) => `/g/${encodeURIComponent(g)}/contrats-artistes`;
const fiche = (g: string, id: string) => `${racine(g)}/${encodeURIComponent(id)}`;
export const contratsArtistesApi = {
  lister: (g: string, f: FiltresContratsArtistes = {}, a?: AppelApi) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined) p.set(k, String(v));
    return api.get<{ contrats: ContratArtistePresente[]; pagination: Pagination }>(
      `${racine(g)}?${p}`,
      { galerie: g, ...a },
    );
  },
  obtenir: (g: string, id: string, a?: AppelApi) =>
    api.get<{ contrat: ContratArtistePresente }>(fiche(g, id), { galerie: g, ...a }),
  creer: (g: string, corps: CorpsContratArtiste, a?: AppelApi) =>
    api.post<{ contrat: ContratArtistePresente }>(racine(g), corps, { galerie: g, ...a }),
  modifier: (
    g: string,
    id: string,
    corps: Partial<Omit<CorpsContratArtiste, 'artisteId'>>,
    a?: AppelApi,
  ) => api.patch<{ contrat: ContratArtistePresente }>(fiche(g, id), corps, { galerie: g, ...a }),
  vigueur: (g: string, id: string, a?: AppelApi) =>
    api.post<{ contrat: ContratArtistePresente }>(
      `${fiche(g, id)}/vigueur`,
      {},
      { galerie: g, ...a },
    ),
  resilier: (g: string, id: string, corps: { motif: string; resilieLe?: string }, a?: AppelApi) =>
    api.post<{ contrat: ContratArtistePresente }>(`${fiche(g, id)}/resilier`, corps, {
      galerie: g,
      ...a,
    }),
  lierDocument: (g: string, id: string, documentId: string | null, a?: AppelApi) =>
    api.put<{ contrat: ContratArtistePresente }>(
      `${fiche(g, id)}/document`,
      { documentId },
      { galerie: g, ...a },
    ),
  lierContratGenere: (g: string, id: string, contratId: string | null, a?: AppelApi) =>
    api.put<{ contrat: ContratArtistePresente }>(
      `${fiche(g, id)}/contrat-genere`,
      { contratId },
      { galerie: g, ...a },
    ),
};
export function useContratsArtistes(g: string, f: FiltresContratsArtistes = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats-artistes', 'liste', f),
    queryFn: (c) => contratsArtistesApi.lister(g, f, appelDeRequete(c)),
  });
}
function invalider(q: ReturnType<typeof useQueryClient>, g: string) {
  void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats-artistes') });
  void q.invalidateQueries({ queryKey: cleMetier(g, 'artistes') });
}
export function useCreerContratArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsContratArtiste, a) => contratsArtistesApi.creer(g, v, a),
      onSuccess: () => invalider(q, g),
    }),
  );
}
export function useModifierContratArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; corps: Partial<Omit<CorpsContratArtiste, 'artisteId'>> }, a) =>
        contratsArtistesApi.modifier(g, v.id, v.corps, a),
      onSuccess: () => invalider(q, g),
    }),
  );
}
export function useGesteContratArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        v: {
          id: string;
          geste: 'vigueur' | 'resilier' | 'document' | 'contrat-genere';
          motif?: string;
          resilieLe?: string;
          documentId?: string | null;
          contratId?: string | null;
        },
        a,
      ) => {
        switch (v.geste) {
          case 'vigueur':
            return contratsArtistesApi.vigueur(g, v.id, a);
          case 'resilier':
            return contratsArtistesApi.resilier(
              g,
              v.id,
              { motif: v.motif ?? '', ...(v.resilieLe ? { resilieLe: v.resilieLe } : {}) },
              a,
            );
          case 'document':
            return contratsArtistesApi.lierDocument(g, v.id, v.documentId ?? null, a);
          case 'contrat-genere':
            return contratsArtistesApi.lierContratGenere(g, v.id, v.contratId ?? null, a);
        }
      },
      onSuccess: () => invalider(q, g),
    }),
  );
}

export function useTousContratsArtistes(
  g: string,
  f: Omit<FiltresContratsArtistes, 'page' | 'parPage'> = {},
) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats-artistes', 'tous', f),
    queryFn: async (c) => {
      const a = appelDeRequete(c);
      const contrats: ContratArtistePresente[] = [];
      let page = 1;
      let pages: number;
      do {
        const r = await contratsArtistesApi.lister(g, { ...f, page, parPage: 200 }, a);
        contrats.push(...r.contrats);
        pages = r.pagination.pages;
        page++;
      } while (page <= pages);
      return contrats;
    },
  });
}
